/**
 * My Bids Page
 * View provider's submitted bids and their status
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { marketplaceService } from '../../services/marketplace.service';
import type { Bid } from '../../types/marketplace';
import { toast } from '@/lib/utils/toast';

export function MyBids() {
  const [bids, setBids] = useState<Bid[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');

  const loadBids = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await marketplaceService.getMyBids(statusFilter || undefined);
      setBids(response.bids);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bids');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadBids();
  }, [loadBids]);

  const handleWithdrawBid = async (bidId: string) => {
    if (!confirm('Are you sure you want to withdraw this bid?')) {
      return;
    }

    try {
      await marketplaceService.withdrawBid(bidId);
      await loadBids(); // Refresh list
      toast.success('Bid withdrawn successfully');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to withdraw bid');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2">My Bids</h1>
        <p className="text-neutral-600">Track your submitted bids and their status</p>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <label htmlFor="status" className="block text-sm font-medium text-neutral-700 mb-2">
          Filter by Status
        </label>
        <select
          id="status"
          className="input max-w-xs"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Bids</option>
          <option value="pending">Pending</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
          <option value="withdrawn">Withdrawn</option>
          <option value="awarded">Awarded</option>
        </select>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-neutral-600">Loading bids...</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="card bg-red-50 border-red-200">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Bids list */}
      {!loading && !error && (
        <div className="space-y-4">
          {bids.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-neutral-600 mb-4">No bids found.</p>
              <Link to="/provider/marketplace" className="btn-primary">
                Browse Jobs
              </Link>
            </div>
          ) : (
            bids.map((bid) => (
              <div key={bid.id} className="card">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <Link
                      to={`/provider/marketplace/jobs/${bid.jobId}`}
                      className="text-lg font-semibold text-primary-600 hover:text-primary-700"
                    >
                      View Job
                    </Link>
                    <p className="text-sm text-neutral-600 mt-1">
                      Submitted {new Date(bid.createdAt * 1000).toLocaleDateString()}
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      bid.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : bid.status === 'accepted'
                        ? 'bg-green-100 text-green-800'
                        : bid.status === 'awarded'
                        ? 'bg-blue-100 text-blue-800'
                        : bid.status === 'rejected'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-neutral-100 text-neutral-800'
                    }`}
                  >
                    {bid.status}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-neutral-600">Estimated Total</p>
                    <p className="text-lg font-semibold text-neutral-900">
                      {formatCurrency(bid.pricing.estimatedTotal)}
                    </p>
                  </div>
                  {bid.pricing.perTonPrice && (
                    <div>
                      <p className="text-sm text-neutral-600">Per-Ton Price</p>
                      <p className="text-lg font-semibold text-neutral-900">
                        {formatCurrency(bid.pricing.perTonPrice)}
                      </p>
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <p className="text-sm text-neutral-600 mb-1">Service Description</p>
                  <p className="text-neutral-900">{bid.serviceDescription}</p>
                </div>

                {bid.message && (
                  <div className="mb-4">
                    <p className="text-sm text-neutral-600 mb-1">Message</p>
                    <p className="text-neutral-900">{bid.message}</p>
                  </div>
                )}

                {bid.status === 'pending' && (
                  <div className="flex justify-end">
                    <button
                      onClick={() => handleWithdrawBid(bid.id)}
                      className="btn-secondary text-red-600 hover:bg-red-50"
                    >
                      Withdraw Bid
                    </button>
                  </div>
                )}

                {bid.status === 'accepted' && bid.depositPaymentId && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                    <p className="text-sm text-green-800">
                      Deposit payment received. Job will begin soon.
                    </p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
